import type { MigrationConsistency } from '@devrobot/shared';
import { FlaskConical, Info, PauseCircle, PlayCircle, TriangleAlert } from 'lucide-react';
import { SkeletonLines } from '../../../components/Skeleton';
import { DOWNTIME_TEXT } from '../PlanSummary';
import { destinationDataConflicts, kindOf, type StepProps } from './model';

const OPTIONS: { value: MigrationConsistency; title: string; text: string; icon: typeof PauseCircle }[] = [
  {
    value: 'stop-source',
    title: 'Stop the source while copying',
    text: 'Recommended. Services that write the copied data stop for the transfer, so the copy is consistent. They start again afterwards.',
    icon: PauseCircle,
  },
  {
    value: 'keep-running',
    title: 'Keep the source running',
    text: 'No downtime, but volumes copied while the application writes to them may be inconsistent. Databases use a logical dump where supported.',
    icon: PlayCircle,
  },
];

export function StrategyStep({ state, dispatch, preview }: StepProps) {
  const plan = preview.plan;
  if (!plan) return <SkeletonLines lines={6} />;
  const serverMove = kindOf(state) === 'server-move';
  const conflicts = destinationDataConflicts(plan, state);
  const showOverwrite = plan.destination.hasData || conflicts.length > 0;

  return (
    <div className="space-y-6">
      <fieldset role="radiogroup" aria-label="Consistency">
        <legend className="mb-2 text-sm font-semibold">Consistency</legend>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {OPTIONS.map((o) => {
            const checked = state.consistency === o.value;
            return (
              <label key={o.value} className={`card dr-surface cursor-pointer border-2 p-4 transition-colors ${checked ? 'border-primary' : 'border-transparent'}`}>
                <span className="flex items-start gap-3">
                  <input
                    type="radio"
                    name="migration-consistency"
                    className="radio radio-primary radio-sm mt-1"
                    checked={checked}
                    onChange={() => dispatch({ type: 'set', patch: { consistency: o.value } })}
                  />
                  <span className="min-w-0 space-y-1">
                    <span className="flex items-center gap-1.5 font-semibold">
                      <o.icon className="size-4" aria-hidden="true" /> {o.title}
                    </span>
                    <span className="block text-sm text-base-content/70">{o.text}</span>
                  </span>
                </span>
              </label>
            );
          })}
        </div>
        <p className={`mt-3 flex items-start gap-2 text-sm transition-opacity ${preview.upToDate ? '' : 'opacity-60'}`} aria-live="polite">
          <Info className="mt-0.5 size-4 shrink-0 text-base-content/55" aria-hidden="true" />
          <span>
            <span className="font-medium">Expected downtime: </span>
            {DOWNTIME_TEXT[plan.estimatedDowntime]}
          </span>
        </p>
      </fieldset>

      {plan.warnings.length > 0 ? (
        <div role="status" className="alert alert-warning alert-soft items-start text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <ul className="list-disc pl-4">
            {plan.warnings.map((w, i) => (
              <li key={i} className="break-anywhere">
                {w}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showOverwrite ? (
        <div className="space-y-3 rounded-field border border-error/40 p-4">
          <div role="alert" className="alert alert-error alert-soft items-start text-sm">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              The destination already holds data{conflicts.length ? ` for ${conflicts.join(', ')}` : ''}. Migrating replaces it with the source data; what is there now is lost unless you have a backup.
            </span>
          </div>
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="toggle toggle-error toggle-sm mt-0.5"
              checked={state.overwriteDestination}
              onChange={(e) => dispatch({ type: 'set', patch: { overwriteDestination: e.target.checked } })}
            />
            <span className="font-medium">Replace the destination data</span>
          </label>
        </div>
      ) : null}

      {serverMove ? (
        <div className="space-y-1 rounded-field bg-base-200/60 p-4">
          <label className="flex cursor-pointer items-start gap-3 text-sm">
            <input type="checkbox" className="toggle toggle-sm mt-0.5" checked={state.cutover} onChange={(e) => dispatch({ type: 'set', patch: { cutover: e.target.checked } })} />
            <span>
              <span className="font-medium">Move hostnames to the new VPS and stop the source after success</span>{' '}
              <span className="badge badge-soft badge-accent badge-xs gap-1 align-middle">
                <FlaskConical className="size-3" aria-hidden="true" /> experimental
              </span>
              <span className="block text-base-content/65">
                Without it, the destination starts with generated hostnames and the source keeps serving; you switch traffic yourself when you are ready. DNS for the moved hostnames must point to the new VPS.
              </span>
            </span>
          </label>
        </div>
      ) : null}
    </div>
  );
}
